/**
 * What the books hold besides the documents, as plain bookings from one Sachkonto to another for
 * the accountant's import: statement rows booked without a receipt (bank charges, SVS, private,
 * the Finanzamt, transfers), the year's AfA and the Fahrtenbuch. The lines come from the same
 * functions the journal posts with (`lib/ledger/money.ts`), so the file and the journal agree.
 *
 * Left out on purpose: payments of linked invoices (the Kanzlei clears its open items from its own
 * bank import) and opening balances (it carries its own). A row that is explained but cannot be
 * posted - a category without account number, a foreign currency - is a problem, never guessed.
 */
import type { BankAccount, Transaction } from "@/lib/api/banking";
import type { Category } from "@/lib/api/categories";
import { isWithin, type Period } from "@/lib/at/periods";
import { kmBefore } from "@/lib/at/travel";
import {
  depreciationPosting,
  moneyAccount,
  transactionPosting,
  tripPosting,
  type LedgerAsset,
  type LedgerTrip,
  type MoneyProblem,
} from "@/lib/ledger/money";
import type { Posting } from "@/lib/ledger/posting";

export type PlainBooking = {
  /** ISO day. */
  date: string;
  debit: string;
  credit: string;
  /** Euros, positive. */
  amount: number;
  /** BK for a bank or card row, KA for the till, UB for AfA and trips (Umbuchung). */
  symbol: "BK" | "KA" | "UB";
  /** Digits only, as BMD and RZL want them: the day, YYYYMMDD. */
  belegnr: string;
  text: string;
};

export type MoneyBookingProblem = {
  transactionId: string;
  date: string;
  text: string;
  reason: MoneyProblem;
};

export type MoneyBookingInput = {
  period: Period;
  transactions: Transaction[];
  accounts: BankAccount[];
  categories: Category[];
  assets: LedgerAsset[];
  trips: Array<LedgerTrip & { id: string }>;
  cashAccount: string;
};

/**
 * A posting as bookings of one debit against one credit. Every money posting has a single line
 * on one side (the bank, Privat, the asset account), so each line of the other side becomes one
 * booking against it.
 */
export function pairLines(
  posting: Posting,
): Array<{ debit: string; credit: string; cents: number }> {
  const debits = posting.lines.filter((line) => line.debit > 0);
  const credits = posting.lines.filter((line) => line.credit > 0);
  if (credits.length === 1) {
    return debits.map((line) => ({
      debit: line.account,
      credit: credits[0]!.account,
      cents: line.debit,
    }));
  }
  if (debits.length === 1) {
    return credits.map((line) => ({
      debit: debits[0]!.account,
      credit: line.account,
      cents: line.credit,
    }));
  }
  throw new Error("A money posting has one line on one side");
}

const digits = (date: string) => date.slice(0, 10).replace(/-/g, "");

export function moneyBookings(input: MoneyBookingInput): {
  bookings: PlainBooking[];
  problems: MoneyBookingProblem[];
} {
  const bookings: PlainBooking[] = [];
  const problems: MoneyBookingProblem[] = [];
  const accounts = new Map(input.accounts.map((account) => [account.id, account]));
  const categories = new Map(input.categories.map((category) => [category.id, category]));
  const ledgerOf = (id: string) => {
    const account = accounts.get(id);
    return account ? moneyAccount(account, input.cashAccount) : "";
  };
  const add = (posting: Posting, symbol: PlainBooking["symbol"], text: string) => {
    for (const pair of pairLines(posting)) {
      bookings.push({
        date: posting.date,
        debit: pair.debit,
        credit: pair.credit,
        amount: pair.cents / 100,
        symbol,
        belegnr: digits(posting.date),
        text,
      });
    }
  };

  for (const row of input.transactions) {
    // Rows linked to a document are its payment or the Barbeleg itself: in the document's booking.
    if (row.status !== "ignored" || !row.bookingType) continue;
    if (!isWithin(row.bookingDate, input.period)) continue;
    const account = accounts.get(row.account);
    if (!account) continue;
    const result = transactionPosting(row, {
      account: moneyAccount(account, input.cashAccount),
      openingDate: (account.openingDate ?? "").slice(0, 10) || undefined,
      categoryAccount: row.category ? categories.get(row.category)?.accountNumber : undefined,
      transferAccount: row.transferAccount ? ledgerOf(row.transferAccount) : undefined,
    });
    const text = [row.counterparty, row.bookingNote].filter(Boolean).join(" · ");
    if (result && "problem" in result) {
      problems.push({
        transactionId: row.id,
        date: row.bookingDate.slice(0, 10),
        text: row.counterparty ?? "",
        reason: result.problem,
      });
    } else if (result) {
      add(result, account.type === "cash" ? "KA" : "BK", text || row.description || "");
    }
  }

  const first = Number(input.period.from.slice(0, 4));
  const last = Number(input.period.to.slice(0, 4));
  for (const asset of input.assets) {
    for (let year = first; year <= last; year++) {
      const posting = depreciationPosting(asset, year);
      if (posting && isWithin(posting.date, input.period)) {
        add(posting, "UB", `AfA ${year} ${asset.name}`);
      }
    }
  }

  for (const trip of input.trips) {
    if (!isWithin(trip.date, input.period)) continue;
    const posting = tripPosting(trip, kmBefore(trip, input.trips));
    if (posting) add(posting, "UB", posting.partnerName);
  }

  bookings.sort((a, b) => a.date.localeCompare(b.date));
  return { bookings, problems };
}
